<template>
  <div>
    <!-- Общие для всей группы данные: страна выбирается ОДИН раз -->
    <v-card
      outlined
      class="mb-6"
    >
      <v-card-text>
        <p class="text-sm font-weight-semibold mb-3">
          {{ $t('Данные группы (общие для всех музеев)') }}
        </p>
        <!-- У китайских и российских групп страна не выбирается. -->
        <p
          v-if="isChinaGroup"
          class="text-sm mt-2 mb-4"
        >
          {{ $t('Страна') }}: <span class="font-weight-semibold">{{ $t('Китай') }}</span>
        </p>
        <p
          v-else-if="isRussiaGroup"
          class="text-sm mt-2 mb-4"
        >
          {{ $t('Страна') }}: <span class="font-weight-semibold">{{ $t('Россия') }}</span>
        </p>
        <v-select
          v-else
          v-model="country"
          :items="filteredCountries"
          :rules="[v => !!v || $t('Выберите страну')]"
          :label="$t('Выберите страну')"
          required
          hide-details="auto"
          item-text="name"
          item-value="system_id"
          :no-data-text="$t('Ничего не найдено')"
          class="mt-2 mb-4"
          outlined
          dense
          @change="countrySearch = ''"
        >
          <template v-slot:prepend-item>
            <v-list-item>
              <v-list-item-content class="py-1">
                <v-text-field
                  v-model="countrySearch"
                  :placeholder="$t('Поиск')"
                  outlined
                  dense
                  hide-details
                  clearable
                  @click.stop
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mb-1"></v-divider>
          </template>
        </v-select>
        <v-text-field
          v-model="guideName"
          type="text"
          outlined
          dense
          class="mt-2 mb-4"
          :label="$t('Имя гида')"
          :placeholder="$t('Имя гида')"
          hide-details="auto"
        ></v-text-field>
        <v-text-field
          v-model="guidePhone"
          type="text"
          outlined
          dense
          class="mt-2 mb-4"
          :label="$t('Телефон гида')"
          :placeholder="$t('Телефон гида')"
          hide-details="auto"
        ></v-text-field>
        <!-- Ref# один на весь заезд: у каждого музея к нему добавится свой
             порядковый номер (12345-1, 12345-2, 12345-3). -->
        <v-text-field
          v-model="groupRefNumber"
          type="text"
          outlined
          dense
          class="mt-2"
          :label="$t('Ref номер группы (необязательно)')"
          :placeholder="$t('Ref номер группы (необязательно)')"
          :hint="$t('Один номер на всю группу — к каждому музею добавится -1, -2, -3. Оставьте пустым, и мы подставим свой.')"
          persistent-hint
          hide-details="auto"
        ></v-text-field>
      </v-card-text>
    </v-card>

    <!-- Уже добавленные музеи группы -->
    <v-card
      v-if="entries.length"
      outlined
      class="mb-6"
    >
      <v-card-text>
        <p class="text-sm font-weight-semibold mb-3">
          {{ $t('Музеи в этой группе') }}: {{ entries.length }}
        </p>
        <v-list
          dense
          class="py-0"
        >
          <v-list-item
            v-for="(entry, idx) in entries"
            :key="idx"
            class="px-0"
          >
            <v-list-item-content>
              <v-list-item-title class="text-sm font-weight-semibold">
                {{ idx + 1 }}. {{ entry.museumName }} — {{ entry.objectName }}
                <span
                  v-if="idx === editingIdx"
                  class="text-xs primary--text"
                >({{ $t('правится ниже') }})</span>
              </v-list-item-title>
              <v-list-item-subtitle class="text-xs">
                {{ entry.date }} {{ entry.timeLabel }} · {{ entry.personsCount }} {{ $t('чел.') }} · {{ entry.serviceLabel }}<template v-if="entry.closedDay">
                  · {{ $t('по спец прайсу') }}
                </template>
                <!-- Стоимость по прайс-листу у каждого музея (шеф, 30.10.2026). -->
                <template v-if="entry.amount !== null">
                  · {{ formatMoney(entry.amount) }}
                </template>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="flex-row align-center">
              <v-btn
                icon
                small
                color="primary"
                :title="$t('Изменить')"
                @click="editEntry(idx)"
              >
                <v-icon size="20">
                  {{ icons.mdiPencil }}
                </v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                :title="$t('Убрать')"
                @click="removeEntry(idx)"
              >
                <v-icon size="20">
                  {{ icons.mdiClose }}
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <!-- Общий итог по добавленным музеям: считается то, что оценено по
             прайс-листу; музеи без цены в сумму не входят. -->
        <p
          v-if="entriesTotal.counted"
          class="text-sm mt-3 mb-0"
        >
          <span class="text--secondary">{{ $t('Стоимость по прайс-листу:') }} </span>
          <span class="font-weight-semibold text--primary">{{ formatMoney(entriesTotal.total) }}</span>
          <span
            v-if="entriesTotal.counted < entries.length"
            class="text--secondary"
          > ({{ $t('оценено музеев: {n} из {total}', { n: entriesTotal.counted, total: entries.length }) }})</span>
        </p>
      </v-card-text>
    </v-card>

    <!-- Текущий музей (черновик) -->
    <v-card
      outlined
      class="mb-6"
    >
      <v-card-text>
        <p class="text-sm font-weight-semibold mb-4">
          <template v-if="editingIdx !== null">
            {{ $t('Изменение музея') }} № {{ editingIdx + 1 }}
          </template>
          <template v-else>
            {{ entries.length ? $t('Добавить ещё музей') : $t('Первый музей группы') }}
          </template>
        </p>
        <v-form
          ref="draftForm"
          v-model="draftFormValid"
          lazy-validation
        >
          <v-select
            v-model="draft.museum"
            :items="filteredMuseumsList"
            :rules="[v => !!v || $t('Выберите музей')]"
            :label="$t('Выберите музей')"
            required
            :item-text="'name' + getFormatedLocale()"
            item-value="id"
            :no-data-text="$t('Ничего не найдено')"
            hide-details="auto"
            class="mb-4 mt-2"
            outlined
            dense
            @change="onMuseumChange"
          >
            <template v-slot:prepend-item>
              <v-list-item>
                <v-list-item-content class="py-1">
                  <v-text-field
                    v-model="museumSearch"
                    :placeholder="$t('Поиск')"
                    outlined
                    dense
                    hide-details
                    clearable
                    @click.stop
                  ></v-text-field>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mb-1"></v-divider>
            </template>
          </v-select>

          <v-select
            v-model="draft.museumObject"
            :items="museumObjectsList"
            :rules="[v => !!v || $t('Выберите объект')]"
            :label="$t('Выберите объект')"
            required
            :item-text="'name' + getFormatedLocale()"
            item-value="system_id"
            :no-data-text="$t('Сначала выберите музей')"
            hide-details="auto"
            class="mb-2 mt-2"
            outlined
            dense
            @change="onObjectChange"
          ></v-select>

          <p
            v-if="selectedMuseumObject.info_message"
            class="caption"
          >
            {{ selectedMuseumObject.info_message }}
          </p>

          <div
            v-if="draft.museumObject"
            class="mt-4"
          >
            <datepicker
              :key="datepickerKey"
              on-page="new-request-date"
              :date-value="draft.date"
              :min-days-before="Number(selectedMuseumObject.min_days_before || 0)"
              :days-off-dates="draft.closedDay ? [] : (selectedMuseumObject['days_off_dates'] || [])"
              :days-off="draft.closedDay ? [] : (selectedMuseumObject['days_off'] || [])"
              @newDate="onDateChange($event)"
            ></datepicker>

            <p class="caption mt-2 mb-1">
              {{ $t('Если Вы хотите экскурсию в день, недоступный для выбора в календаре (выходной, праздничный) — поставьте галочку, календарь откроется. Действует специальный прайс. Наш менеджер свяжется с Вами и уточнит детали.') }}
            </p>
            <v-checkbox
              v-model="draft.closedDay"
              hide-details
              dense
              class="mt-0 pt-0"
              :label="$t('Да, хочу экскурсию в другой день')"
              @change="onClosedDayToggle"
            ></v-checkbox>

            <v-select
              v-model="draft.schedule"
              :items="scheduleList"
              :rules="[v => !!v || $t('Выберите время')]"
              :label="$t('Выберите время')"
              required
              item-text="name"
              item-value="id"
              hide-details="auto"
              class="mb-2 mt-4"
              outlined
              dense
            ></v-select>
            <p
              v-if="!scheduleList.length"
              class="caption mt-2 mb-0"
              style="color: crimson;"
            >
              {{ $t('На выбранную дату нет доступного времени — выберите другую дату.') }}
            </p>
          </div>

          <!-- Количество билетов -->
          <div
            v-if="draft.museumObject"
            class="mt-2"
          >
            <div class="d-flex align-center mb-2">
              <span class="text-sm font-weight-semibold">{{ $t('Количество билетов') }}</span>
              <v-spacer></v-spacer>
              <v-checkbox
                v-if="entries.length"
                v-model="sameAsPrevious"
                dense
                hide-details
                class="mt-0 pt-0"
                :label="$t('Как в предыдущей группе')"
                @change="onSameAsPrevious"
              ></v-checkbox>
            </div>
            <v-text-field
              v-for="kindOfTicket in selectedMuseumObject.tickets"
              :id="kindOfTicket.internal_name"
              :key="kindOfTicket.id"
              v-model="draft.tickets[kindOfTicket.internal_name]"
              v-non-negative
              type="number"
              outlined
              dense
              class="mt-2 mb-2"
              :label="kindOfTicket['name' + getFormatedLocale()]"
              :hint="kindOfTicket['helper_text' + getFormatedLocale()]"
              placeholder="0"
              :rules="[validators.integerPositiveValidator]"
              persistent-hint
            ></v-text-field>
            <!-- Стоимость по прайс-листу (шеф, 30.10.2026: «пусть ведётся расчёт
                 при подаче множественной заявки»): тот же серверный расчёт, что в
                 обычной заявке. Объект не оценён — строки нет. -->
            <p
              v-if="draftQuote.amount !== null && personsCount > 0"
              class="text-sm mt-2 mb-0"
            >
              <span class="text--secondary">{{ $t('Стоимость по прайс-листу:') }} </span>
              <span class="font-weight-semibold text--primary">{{ formatMoney(draftQuote.amount) }}</span>
            </p>
          </div>

          <!-- Радиогид / Аудиогид -->
          <div
            v-if="draft.museumObject && (rgAvailable || agAvailable)"
            class="mt-6"
          >
            <!-- Та же подпись, что в «Расчёте стоимости» (шеф, 02.11.2026):
                 появляется, когда группа дошла до порога объекта. -->
            <p
              v-if="rgMandatory"
              class="caption mb-1 error--text font-weight-semibold"
            >
              {{ $t('При группе от {n} человек требуется радиогид', { n: rgThreshold }) }}
            </p>
            <!-- Взаимоисключающие галочки, ничего не выбрано по умолчанию:
                 отметка одной снимает другую (через onServiceToggle). Название и
                 подсказка — из справочника услуг, как в обычной заявке. -->
            <v-checkbox
              v-if="rgAvailable"
              :input-value="draft.service === 'rg'"
              hide-details
              class="mt-2 mb-4"
              @change="val => onServiceToggle('rg', val)"
            >
              <template #label>
                <div>
                  {{ serviceLabel('rg') || $t('Радиогид') }}
                  <v-tooltip
                    v-if="serviceHint('rg')"
                    top
                  >
                    <template #activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        size="20"
                        class="ml-2 info--secondary"
                        v-on="on"
                      >
                        {{ icons.mdiHelpCircleOutline }}
                      </v-icon>
                    </template>
                    <span>{{ serviceHint('rg') }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-checkbox>
            <v-checkbox
              v-if="agAvailable"
              :input-value="draft.service === 'ag'"
              hide-details
              class="mt-2 mb-4"
              @change="val => onServiceToggle('ag', val)"
            >
              <template #label>
                <div>
                  {{ serviceLabel('ag') || $t('Аудиогид') }}
                  <v-tooltip
                    v-if="serviceHint('ag')"
                    top
                  >
                    <template #activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        size="20"
                        class="ml-2 info--secondary"
                        v-on="on"
                      >
                        {{ icons.mdiHelpCircleOutline }}
                      </v-icon>
                    </template>
                    <span>{{ serviceHint('ag') }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-checkbox>
          </div>

          <!-- Кол-во гидов-сопровождающих: как в обычной заявке — спрашиваем
               всегда, если объект это поле показывает, и независимо от того,
               выбран радиогид, аудиогид или ничего (речь про лицензию гида, а не
               про оборудование). Поэтому блок отдельный, а не внутри услуг.
               Подпись и пояснение берём из карточки объекта в админке. -->
          <!-- Количество считается само по размеру группы (шеф, 17.10.2026). -->
          <v-text-field
            v-if="draft.museumObject && selectedMuseumObject.is_additional_guide_field"
            :value="draft.guides"
            readonly
            outlined
            dense
            class="mt-4 mb-2"
            :label="guideFieldText('additional_guide_label') || $t('Кол-во гидов сопровождающих (если нет лицензии)')"
            :hint="autoGuidesHint"
            persistent-hint
            hide-details="auto"
          ></v-text-field>

          <v-textarea
            v-if="draft.museumObject"
            v-model="draft.comment"
            :label="$t('Пожелания')"
            :placeholder="$t('Пожелания по этому музею')"
            rows="3"
            outlined
            hide-details="auto"
            class="mt-6"
          ></v-textarea>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Кнопки действий держим справа — единое правило по всему кабинету. -->
    <div
      class="d-flex flex-wrap justify-end"
      style="gap: 8px"
    >
      <!-- Кнопка НЕ блокируется: по нажатию показываем, каких полей не хватает. -->
      <v-btn
        color="secondary"
        outlined
        @click="saveDraft"
      >
        {{ editingIdx !== null ? $t('Сохранить изменения') : $t('Сохранить и добавить музей') }}
      </v-btn>
      <v-btn
        v-if="editingIdx !== null"
        text
        @click="cancelEdit"
      >
        {{ $t('Отменить правку') }}
      </v-btn>
      <v-btn
        v-else-if="!isDraftEmpty"
        text
        @click="clearDraft"
      >
        {{ $t('Очистить') }}
      </v-btn>
      <v-btn
        v-if="canSubmit"
        color="primary"
        @click="submitAll"
      >
        {{ $t('Отправить') }} ({{ submitCount }})
      </v-btn>
    </div>
    <p
      v-if="saveAttempted && missingFields.length"
      class="caption mt-2 mb-0 error--text text-right"
    >
      {{ $t('Заполните') }}: {{ missingFields.join(', ') }}
    </p>
    <p class="caption mt-2 text--secondary text-right">
      {{ $t('Добавьте столько музеев, сколько нужно группе, и нажмите «Отправить» — на каждый музей уйдёт отдельная заявка.') }}
    </p>

    <!-- Оверлей отправки -->
    <v-dialog
      v-model="submitting"
      overlay
      persistent
      width="300"
    >
      <v-card
        color="primary"
        dark
      >
        <v-card-text class="pt-3 white--text">
          {{ $t('Пожалуйста, ожидайте') }}
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0 mt-2"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Результат -->
    <v-dialog
      v-model="showResult"
      width="500"
    >
      <v-card>
        <v-card-title v-if="resultOk">
          {{ $t('Успешно') }}
        </v-card-title>
        <v-card-title v-else>
          {{ $t('Заявка не отправлена') }}
        </v-card-title>
        <!-- pre-line: причину показываем построчно, одной простынёй её не прочесть -->
        <v-card-text style="white-space: pre-line">
          {{ resultMsg }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            :color="resultOk ? 'primary' : 'error'"
            @click="onResultClose"
          >
            {{ $t('Ок') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Datepicker from '@/components/Datepicker.vue'
import store from '@/store'
import { useRouter } from '@core/utils'
import {
  earliestRequestDate,
  groupRefNo,
  guideServicesCount,
  makeRefNo,
  refNoBase,
  SCHOOL_NOTE_MARK,
  toLocalDay,
} from '@/utils/helper'
import {
  emptyQuote, fetchRequestQuote, formatMoney, sumQuotes,
} from '@/utils/priceQuote'
import { integerPositiveValidator } from '@core/utils/validation'
import { mdiClose, mdiHelpCircleOutline, mdiPencil } from '@mdi/js'
import { computed, ref, watch } from '@vue/composition-api'

export default {
  components: {
    Datepicker,
  },
  props: {
    // Базовая категория группы: 'ru' | 'en' | 'cn'. В офис уходит именно она.
    baseType: {
      type: String,
      required: true,
    },

    // Галочка «Школьная группа» из основной формы: пометка встаёт в примечание
    // каждой заявки группы при отправке (12.10.2026).
    school: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { root }) {
    const { router } = useRouter()
    const userData = JSON.parse(localStorage.getItem('userData')) || {}
    const todayStr = toLocalDay()

    const getFormatedLocale = () => {
      const locale = localStorage.getItem('locale') || 'ru'
      if (locale === 'zh') return '_zh_cn'
      if (locale === 'en') return '_en_US'
      if (locale === 'ru') return '_ru_RU'
      if (locale === 'fa') return '_fa_IR'
      if (locale === 'vi') return '_vi'

      return '_ru_RU'
    }

    // --- общие данные группы ---
    // Для китайских групп страна не выбирается — она всегда Китай (system_id 100
    // в справочнике стран), именно этот код и уходит в офис.
    const CHINA_COUNTRY_ID = 100
    const isChinaGroup = computed(() => props.baseType === 'cn')

    // У российских групп то же самое: страна всегда Россия (267). Раньше в
    // списке были Россия и Беларусь, и выбор только путал.
    const RUSSIA_COUNTRY_ID = 267
    const isRussiaGroup = computed(() => props.baseType === 'ru')

    const country = ref(
      // eslint-disable-next-line no-nested-ternary
      props.baseType === 'cn' ? CHINA_COUNTRY_ID : props.baseType === 'ru' ? RUSSIA_COUNTRY_ID : '',
    )
    const countrySearch = ref('')
    const guideName = ref(userData.use_default_data ? userData.default_guide_name || '' : '')
    const guidePhone = ref(userData.use_default_data ? userData.default_guide_phone || '' : '')

    // Ref# общий на всю группу. Пусто — подставим свой пятизначный при отправке.
    const groupRefNumber = ref('')

    // --- список музеев группы + текущий черновик ---
    const entries = ref([])

    // Стоимость черновика по прайс-листу (шеф, 30.10.2026), см. ниже.
    const draftQuote = ref(emptyQuote())
    const makeEmptyDraft = () => ({
      museum: null,
      museumObject: null,
      system_id: null,
      date: todayStr,
      schedule: null,
      persons_per_guide: null,
      tickets: {},
      service: null,
      guides: '0',
      comment: '',
      closedDay: false,
    })
    const draft = ref(makeEmptyDraft())
    const sameAsPrevious = ref(false)

    // Какой музей списка сейчас правится (null — заполняется новый). Правка идёт
    // ПО МЕСТУ: запись остаётся в списке и обновляется на своей позиции. Раньше
    // её на время правки вынимали из списка, и порядок музеев после сохранения
    // менялся, а недописанная правка терялась совсем.
    const editingIdx = ref(null)

    // Пока подтягиваются объекты музея и график правящейся записи, сохранять
    // нечего: справочник объекта ещё не загружен, и снимок вышел бы неполным.
    const restoring = ref(false)

    // Номер группы (основа Ref#) — один на весь заезд. Держим его в форме, а не
    // создаём заново на каждой отправке: если часть музеев уже ушла, повтор
    // должен попасть в ТУ ЖЕ группу, иначе заезд разъедется надвое в фильтре
    // «Заказов».
    const groupBase = ref('')

    // Сколько номеров уже роздано в этой группе. Счётчик только растёт: после
    // частичной неудачи оставшиеся музеи сохраняют свои номера, а добавленный
    // потом получит следующий, а не займёт чужой.
    const refCounter = ref(0)

    // Попытка сохранить черновик была — показываем, каких полей не хватает.
    const saveAttempted = ref(false)

    const draftForm = ref(null)
    const draftFormValid = ref(true)
    const datepickerKey = ref(0)
    const museumSearch = ref('')

    const submitting = ref(false)
    const showResult = ref(false)
    const resultOk = ref(false)
    const resultMsg = ref('')

    // --- загрузка справочников ---
    store.dispatch('countries/fetchCountries')
    store.commit('museumObjects/init_selected_museum_object')
    const fetchMuseums = () => {
      store.dispatch('museums/fetchMuseums', props.baseType)
    }
    fetchMuseums()

    const selectedMuseumObject = computed(() => store.getters['museumObjects/getSelectedMuseumObject'])

    // Тексты поля «кол-во гидов сопровождающих» правятся в админке у объекта
    // музея (с учётом языка). Пусто — вызывающий подставит текст по умолчанию.
    const guideFieldText = field => {
      const obj = selectedMuseumObject.value || {}

      return obj[`${field}${getFormatedLocale()}`] || obj[field] || ''
    }

    const filteredMuseumsList = computed(() => {
      const list = store.getters['museums/getMuseumsList'] || []
      const query = (museumSearch.value || '').toLowerCase().trim()
      if (!query) return list
      const key = `name${getFormatedLocale()}`

      return list.filter(
        museum => String(museum[key] || '').toLowerCase().includes(query)
          || museum.id === draft.value.museum,
      )
    })

    const museumObjectsList = computed(() => store.getters['museumObjects/getMuseumObjectsList'])

    const scheduleList = computed(
      () => store.getters['schedule/getSchedule'].filter(item => !item.name.includes('График')),
    )

    const filteredCountries = computed(() => {
      const list = store.getters['countries/getCountries'] || []
      const query = (countrySearch.value || '').toLowerCase().trim()
      if (!query) return list

      return list.filter(
        item => String(item.name || '').toLowerCase().includes(query)
          || item.system_id === country.value,
      )
    })

    // --- каскад выбора внутри черновика ---
    const onMuseumChange = () => {
      draft.value.museumObject = null
      draft.value.system_id = null
      draft.value.tickets = {}
      draft.value.service = null
      store.commit('museumObjects/init_selected_museum_object')
      store.dispatch('museumObjects/fetchMuseumObjects', [draft.value.museum, props.baseType])
    }

    const fetchSchedule = () => {
      store.dispatch('schedule/fetchSchedule', {
        date: draft.value.date,
        museumObjectId: draft.value.museumObject,
      })
    }

    const onObjectChange = () => {
      datepickerKey.value += 1
      draft.value.schedule = null
      draft.value.service = null
      draft.value.guides = '0'
      draft.value.closedDay = false
      sameAsPrevious.value = false
      store.dispatch('museumObjects/setSelectedMuseumObject', draft.value.museumObject).then(obj => {
        const tickets = {}
        obj.tickets.forEach(item => {
          tickets[item.internal_name] = 0
        })
        draft.value.tickets = tickets
        draft.value.persons_per_guide = obj.persons_per_guide
        draft.value.system_id = obj.system_id

        // Самая ранняя допустимая дата для этого объекта (срок подачи из админки).
        draft.value.date = earliestRequestDate(obj)
        fetchSchedule()
      })
    }

    const onDateChange = event => {
      draft.value.date = event.date
      draft.value.schedule = null
      fetchSchedule()
    }

    // Галочка «спец. дата»: как в одиночной форме — при включении календарь
    // раскрывает выходные/праздничные дни объекта (действует спец. прайс, менеджер
    // уточнит детали). Пересобираем календарь и сбрасываем выбор даты/времени,
    // чтобы нельзя было отправить закрытый день, сняв галочку постфактум.
    const onClosedDayToggle = () => {
      draft.value.date = earliestRequestDate(selectedMuseumObject.value)
      draft.value.schedule = null
      datepickerKey.value += 1
      fetchSchedule()
    }

    // РГ/АГ — необязательный ВЗАИМОИСКЛЮЧАЮЩИЙ выбор. Ничего не проставляем
    // автоматически: клиент сам ставит галку, если нужен радиогид ИЛИ аудиогид,
    // либо не ставит ни одной (ведёт лицензионный гид). При радиогиде
    // сопровождающие не нужны — обнуляем их количество.
    const onServiceToggle = (which, checked) => {
      // Сопровождающие живут отдельным полем и от выбора услуги не зависят —
      // здесь только переключаем взаимоисключающие галочки.
      draft.value.service = checked ? which : null
    }

    // Копируем ТОЛЬКО количества билетов из последнего сохранённого музея.
    const onSameAsPrevious = () => {
      if (!sameAsPrevious.value || !entries.value.length) return
      const prev = entries.value[entries.value.length - 1]
      Object.keys(draft.value.tickets).forEach(key => {
        if (Object.prototype.hasOwnProperty.call(prev.tickets, key)) {
          draft.value.tickets[key] = prev.tickets[key]
        }
      })
    }

    // --- производные значения черновика ---
    const personsCount = computed(() => Object.values(draft.value.tickets)
      .reduce((acc, value) => acc + Number(value || 0), 0))

    // Экскурсионное обслуживание считается само: одно на группу до
    // persons_per_guide человек (норма музея у объекта).
    const perGuide = computed(() => Number(draft.value.persons_per_guide || 0))

    // Шеф, 23.10.2026: одно обслуживание на каждые persons_per_guide человек
    // с округлением вверх (40 человек при норме 15 — три); нормы нет — одно
    // на группу. Прежнее «всегда одно на заявку» (17.10.2026) отменено.
    watch([personsCount, perGuide], ([persons, norm]) => {
      draft.value.guides = String(guideServicesCount(persons, norm))
    })

    // Подпись про норму («одно на каждые N человек») убрана 27.10.2026 по
    // просьбе начальника — клиенту это знать не нужно; остаётся только
    // подсказка из настроек объекта, если менеджер её задал.
    const autoGuidesHint = computed(() => guideFieldText('additional_guide_hint') || '')

    // Название и подсказка услуги — из справочника объекта, как в обычной
    // заявке. Для китайских групп аудиогида может не быть в справочнике: тогда
    // вернём пусто, а вёрстка подставит название по умолчанию и скроет подсказку.
    const serviceEntry = internalName => {
      const obj = selectedMuseumObject.value
      const list = (obj && obj.services) || []

      return list.find(item => item.internal_name === internalName) || null
    }
    const serviceLabel = internalName => {
      const item = serviceEntry(internalName)

      return (item && item[`name${getFormatedLocale()}`]) || ''
    }
    const serviceHint = internalName => {
      const item = serviceEntry(internalName)

      return (item && item[`helper_text${getFormatedLocale()}`]) || ''
    }

    const rgAvailable = computed(() => {
      const obj = selectedMuseumObject.value

return !!(obj && obj.services && obj.services.some(s => s.internal_name === 'rg'))
    })

    // Аудиогид: обычно берём из услуг объекта, но китайским группам предлагаем
    // его ВСЕГДА — в справочнике услуга привязана лишь к трём объектам (два
    // Эрмитажа и Екатерининский дворец), а нужен он китайцам повсеместно.
    const agAvailable = computed(() => {
      const obj = selectedMuseumObject.value

return isChinaGroup.value
        || !!(obj && obj.services && obj.services.some(s => s.internal_name === 'ag'))
    })
    const rgThreshold = computed(() => {
      const obj = selectedMuseumObject.value

      return obj ? Math.max(0, Number(obj.amount_for_rg) || 0) : 0
    })
    const rgMandatory = computed(() => rgThreshold.value > 0 && personsCount.value >= rgThreshold.value)

    // Чего не хватает, чтобы сохранить музей в группу — текстом под кнопкой.
    // Раньше кнопка была просто заблокирована и не объясняла, что не так.
    const missingFields = computed(() => {
      const d = draft.value
      const missing = []

      // Страна выбирается один раз на группу и живёт вне проверяемой формы —
      // без этой проверки заявку можно было отправить с пустой страной.
      if (!isChinaGroup.value && !country.value) missing.push('страна группы')
      if (!d.museum) missing.push('музей')
      if (!d.museumObject) missing.push('объект')
      if (!d.date) missing.push('дата')
      if (d.museumObject && !d.schedule) missing.push('время')
      if (d.museumObject && personsCount.value <= 0) missing.push('количество билетов')

      return missing
    })

    // Черновик пустой — клиент к нему ещё не приступал.
    const isDraftEmpty = computed(() => !draft.value.museum && !draft.value.museumObject)

    const isDraftValid = computed(() => {
      const d = draft.value
      if (restoring.value) return false

      // РГ/АГ намеренно НЕ обязателен: может вести лицензионный гид без оборудования.
      if (!d.museum || !d.museumObject || !d.date || !d.schedule) return false
      if (personsCount.value <= 0) return false

      return true
    })

    // Сколько заявок уйдёт. Правящийся музей уже лежит в списке — второй раз его
    // не считаем.
    const submitCount = computed(
      () => entries.value.length + (editingIdx.value === null && isDraftValid.value ? 1 : 0),
    )

    // Отправить можно и одну заявку: иначе после частичной неудачи (осталась
    // одна неотправленная) клиент попадал в тупик — кнопки просто не было.
    const canSubmit = computed(() => submitCount.value >= 1)

    // --- снимок черновика в заявку ---
    // Количество не может быть отрицательным или дробным: правило у поля только
    // подсвечивает ошибку, отправку не блокирует — чистим значения сами.
    const toCount = value => String(Math.max(0, Math.trunc(Number(value) || 0)))

    const cleanTickets = () => {
      const tickets = {}
      Object.keys(draft.value.tickets).forEach(key => {
        tickets[key] = toCount(draft.value.tickets[key])
      })

      return tickets
    }

    const buildServices = () => {
      const obj = selectedMuseumObject.value
      const services = {}
      if (obj && obj.services) {
        obj.services.forEach(s => {
          services[s.internal_name] = false
        })
      }

      // У китайских групп аудиогид предлагаем всегда, поэтому и в заявке ключ
      // должен быть — даже если у объекта такой услуги в справочнике нет.
      if (isChinaGroup.value) {
        services.ag = services.ag || false
      }
      if (draft.value.service) {
        services[draft.value.service] = true
      }

      return services
    }

    const buildSnapshot = () => {
      const locale = getFormatedLocale()
      const obj = selectedMuseumObject.value
      const museum = (store.getters['museums/getMuseumsList'] || []).find(m => m.id === draft.value.museum)
      const scheduleItem = scheduleList.value.find(s => s.id === draft.value.schedule)

      // Короткая подпись для списка добавленных музеев (не путать с serviceLabel()
      // выше — та берёт название услуги из справочника для галочек).
      const guidesCount = toCount(draft.value.guides)
      let entryServiceLabel = guidesCount !== '0' ? `${guidesCount} сопр.` : '—'
      if (draft.value.service === 'rg') entryServiceLabel = 'Радиогид'
      else if (draft.value.service === 'ag') entryServiceLabel = 'Аудиогид'
      if (draft.value.service && guidesCount !== '0') {
        entryServiceLabel += ` + ${guidesCount} сопр.`
      }

      return {
        // поля для офиса
        museum: draft.value.museum,
        system_id: draft.value.system_id,
        date: draft.value.date,
        schedule: draft.value.schedule,
        persons_per_guide: draft.value.persons_per_guide,
        country: country.value,
        guide: guideName.value,
        guidePhone: guidePhone.value,
        tickets: cleanTickets(),
        services: buildServices(),
        guides: toCount(draft.value.guides),

        // Служебные пометки идут первыми (колонка примечаний в офисе узкая), за
        // ними — пожелания клиента. Клиенту на сайте пометки показываются
        // значками, а не в графе «Пожелания» (см. stripServiceMarks).
        notes: [
          draft.value.closedDay ? 'По спец прайсу.' : '',
          'ГРУППОВАЯ ЗАЯВКА',
          String(draft.value.comment || '').trim(),
        ].filter(Boolean).join(' '),
        type: props.baseType,
        closedDay: draft.value.closedDay,

        // поля для отображения в списке и для возврата в форму при правке
        museumName: (museum && museum[`name${locale}`]) || '',
        objectName: (obj && obj[`name${locale}`]) || '',
        timeLabel: (scheduleItem && scheduleItem.name) || '',
        personsCount: personsCount.value,
        serviceLabel: entryServiceLabel,
        service: draft.value.service,
        comment: draft.value.comment,
        museumObject: draft.value.museumObject,

        // стоимость по прайс-листу (уточняется с сервера после сохранения)
        amount: draftQuote.value.amount,
        missing: [...draftQuote.value.missing],
        quoteGuides: toCount(draft.value.guides),
      }
    }

    // --- стоимость по прайс-листу (шеф, 30.10.2026) ---
    // Черновик считается с задержкой, пока человек печатает; сохранённый
    // музей пересчитывается с сервера сразу — чтобы в списке не осталась
    // сумма от недопечатанного количества.
    let quoteTimer = null
    const quotePayload = snapshot => ({
      system_id: snapshot.museumObject,
      pass_date: snapshot.date,
      tickets: snapshot.tickets,
      services: snapshot.services,
      group_type: props.baseType,
      school: false,
      qty_guide: snapshot.quoteGuides,
    })
    const fetchDraftQuote = () => {
      if (!draft.value.museumObject || personsCount.value <= 0) {
        draftQuote.value = emptyQuote()

        return
      }
      const payload = quotePayload({
        museumObject: draft.value.museumObject,
        date: draft.value.date,
        tickets: cleanTickets(),
        services: buildServices(),
        quoteGuides: toCount(draft.value.guides),
      })
      fetchRequestQuote(payload).then(quote => {
        // Пока ждали ответ, объект могли сменить — такой ответ не наш.
        if (payload.system_id !== draft.value.museumObject) return
        draftQuote.value = quote
      })
    }
    watch(
      () => [
        draft.value.museumObject,
        draft.value.date,
        draft.value.guides,
        draft.value.service,
        JSON.stringify(draft.value.tickets),
      ],
      () => {
        window.clearTimeout(quoteTimer)
        quoteTimer = window.setTimeout(fetchDraftQuote, 300)
      },
    )
    const refreshEntryQuote = entry => {
      fetchRequestQuote(quotePayload(entry)).then(quote => {
        const idx = entries.value.indexOf(entry)
        if (idx === -1) return
        entries.value.splice(idx, 1, { ...entry, amount: quote.amount, missing: quote.missing })
      })
    }
    const entriesTotal = computed(() => sumQuotes(entries.value))

    // Название услуги без цены — как в обычной заявке.
    const serviceTitle = name => {
      if (name === 'guide') return root.$t('экскурсионное обслуживание')
      if (name === 'rg') return serviceLabel('rg') || root.$t('Радиогид')
      if (name === 'ag') return serviceLabel('ag') || root.$t('Аудиогид')

      return name
    }

    const resetDraft = () => {
      draft.value = makeEmptyDraft()
      editingIdx.value = null
      saveAttempted.value = false
      sameAsPrevious.value = false
      museumSearch.value = ''
      datepickerKey.value += 1
      store.commit('museumObjects/init_selected_museum_object')
      if (draftForm.value) draftForm.value.resetValidation()
    }

    // Перенести то, что сейчас в форме, в список: правку — на её место, новый
    // музей — в конец.
    const commitDraft = () => {
      const snapshot = buildSnapshot()
      if (editingIdx.value !== null) {
        entries.value.splice(editingIdx.value, 1, snapshot)
      } else {
        entries.value.push(snapshot)
      }
      editingIdx.value = null
      refreshEntryQuote(snapshot)
    }

    const saveDraft = () => {
      // Кнопка активна всегда: по нажатию подсвечиваем незаполненные поля и
      // пишем список под кнопкой — раньше кнопка молча не нажималась.
      if (!isDraftValid.value) {
        saveAttempted.value = true
        if (draftForm.value) draftForm.value.validate()

        return false
      }
      commitDraft()
      saveAttempted.value = false
      resetDraft()

      return true
    }

    const clearDraft = () => {
      resetDraft()
    }

    const cancelEdit = () => {
      // Запись всё это время лежала в списке нетронутой — просто выходим из правки.
      resetDraft()
    }

    // Вернуть сохранённый музей в форму для правки. Восстанавливаем всю цепочку:
    // список объектов музея -> выбранный объект -> график на дату. Из списка
    // запись НЕ убираем — она обновится на своём месте по «Сохранить изменения».
    const editEntry = idx => {
      const entry = entries.value[idx]
      if (!entry || idx === editingIdx.value) return

      // То, что уже набрано в форме, не теряем: заполненное — сохраняем, начатое
      // и недозаполненное — просим сначала завершить или очистить.
      if (!isDraftEmpty.value) {
        if (!isDraftValid.value) {
          saveAttempted.value = true
          if (draftForm.value) draftForm.value.validate()

          return
        }
        commitDraft()
      }

      editingIdx.value = idx
      saveAttempted.value = false
      sameAsPrevious.value = false
      restoring.value = true

      draft.value = {
        museum: entry.museum,
        museumObject: entry.museumObject,
        system_id: entry.system_id,
        date: entry.date,
        schedule: entry.schedule,
        persons_per_guide: entry.persons_per_guide,
        tickets: { ...entry.tickets },
        service: entry.service || null,
        guides: entry.guides || '0',
        comment: entry.comment || '',
        closedDay: !!entry.closedDay,
      }

      store.dispatch('museumObjects/fetchMuseumObjects', [entry.museum, props.baseType])
        .then(() => store.dispatch('museumObjects/setSelectedMuseumObject', entry.museumObject))
        .then(() => {
          store.dispatch('schedule/fetchSchedule', {
            date: entry.date,
            museumObjectId: entry.museumObject,
          })
          datepickerKey.value += 1
          restoring.value = false
        })
        .catch(() => {
          restoring.value = false
        })
    }

    const removeEntry = idx => {
      entries.value.splice(idx, 1)
      if (editingIdx.value === idx) {
        resetDraft()
      } else if (editingIdx.value !== null && editingIdx.value > idx) {
        editingIdx.value -= 1
      }
    }

    const onResultClose = () => {
      showResult.value = false
      if (resultOk.value) {
        router.push({ name: 'upcoming-requests' })
      }
    }

    // Как назвать музей в сообщении об ошибке — чтобы клиент понял, в каком
    // именно музее что-то не так.
    const describeEntry = entry => [
      `${entry.museumName} — ${entry.objectName}`,
      entry.date,
      entry.timeLabel,
    ].filter(Boolean).join(', ')

    // Проверка ПЕРЕД отправкой: у каждого музея перезапрашиваем расписание на
    // выбранную дату и смотрим, живо ли ещё выбранное время. Пока клиент
    // заполнял группу, место могли занять — и заявка уходила бы в офис уже
    // битой. Проверяем ВСЕ музеи и только потом отправляем: иначе половина
    // заезда оказывалась в офисе, а половина — нет.
    const collectProblems = async list => {
      const problems = []
      for (let i = 0; i < list.length; i += 1) {
        const entry = list[i]

        // eslint-disable-next-line no-await-in-loop
        const res = await store.dispatch('createOrder/checkSchedule', {
          date: entry.date,
          museumObjectId: entry.museumObject,
        })

        if (!res.ok) {
          problems.push(`• ${describeEntry(entry)} — ${res.error}`)
        } else if (!(res.schedule || []).some(item => item.id === entry.schedule)) {
          problems.push(
            `• ${describeEntry(entry)} — это время больше недоступно, выберите другую дату или время`,
          )
        }
      }

      return problems
    }

    const submitAll = async () => {
      // Без страны заявка уйдёт в офис пустой — не отправляем.
      if (!isChinaGroup.value && !country.value) {
        saveAttempted.value = true

        return
      }

      // Начатый, но недозаполненный черновик молча не теряем: подсвечиваем его.
      if (!isDraftEmpty.value && !isDraftValid.value) {
        saveAttempted.value = true
        if (draftForm.value) draftForm.value.validate()

        return
      }

      const saved = [...entries.value]
      if (isDraftValid.value) {
        // Правящийся музей обновляем на его месте, новый — дописываем в конец.
        if (editingIdx.value !== null) saved.splice(editingIdx.value, 1, buildSnapshot())
        else saved.push(buildSnapshot())
      }
      if (!saved.length) return

      // Страну и гида берём НА МОМЕНТ ОТПРАВКИ, а не из снимка музея. В снимок
      // они попадают такими, какими были при нажатии «Сохранить и добавить
      // музей», поэтому правка задним числом (исправили опечатку в телефоне,
      // сменили гида или страну) не доезжала до уже добавленных музеев — те
      // уходили в офис со старым значением. Поля общие на всю группу, значит и
      // значение должно быть одно на всех.
      // Ref# всей цепочки: общий номер группы плюс порядковый номер музея —
      // 12345-1, 12345-2, 12345-3. По нему список заявок на главной и выделяет
      // «эту» группу, и оператор в офисе видит, что это один заезд.
      if (!groupBase.value) {
        groupBase.value = groupRefNumber.value.trim() || makeRefNo(
          (store.getters['upcoming/getUpcomingList'] || [])
            .map(request => refNoBase((request.data && request.data.refno) || ''))
            .filter(Boolean),
        )
      }

      const all = saved.map(entry => {
        // Номер, выданный на прошлой попытке, сохраняем — иначе после частичной
        // неудачи музеи перенумеровались бы с единицы.
        const refNumber = entry.refNumber || groupRefNo(groupBase.value, refCounter.value)
        if (!entry.refNumber) refCounter.value += 1

        return {
          ...entry,
          notes: [props.school ? SCHOOL_NOTE_MARK : '', entry.notes].filter(Boolean).join(' '),
          country: country.value,
          guide: guideName.value,
          guidePhone: guidePhone.value,
          refNumber,
        }
      })

      submitting.value = true

      const problems = await collectProblems(all)
      if (problems.length) {
        submitting.value = false
        resultOk.value = false
        resultMsg.value = [
          'Ни одна заявка не отправлена — сначала поправьте:',
          '',
          problems.join('\n'),
          '',
          'Все музеи группы уходят только вместе.',
        ].join('\n')
        showResult.value = true

        return
      }

      const results = await store.dispatch('createOrder/createGroupOrder', all)
      submitting.value = false

      const okResults = results.filter(r => r.ok)
      const failed = results.find(r => !r.ok)

      if (!failed) {
        resultOk.value = true
        const nums = okResults.map(r => `№${r.id}`).join(', ')
        resultMsg.value = `Отправлено заявок: ${okResults.length}. ${nums}`

        // Заявки ушли — на «Заказах» предложим оценить кабинет.
        try {
          sessionStorage.setItem('askFeedback', '1')
        } catch (e) {
          // приватный режим браузера — не беда, просто не спросим
        }
        entries.value = []
        groupBase.value = ''
        refCounter.value = 0
        resetDraft()
      } else {
        resultOk.value = false

        const lines = [
          `${describeEntry(all[failed.index])}`,
          '',
          `Причина: ${failed.error}`,
        ]
        if (okResults.length) {
          // Заявки, ушедшие до сбоя, отозвать нельзя — честно предупреждаем,
          // иначе клиент отправит их повторно и получит дубли в офисе.
          lines.push(
            '',
            `Внимание: до сбоя в офис успели уйти заявки ${okResults.map(r => `№${r.id}`).join(', ')} — они уже в «Заказах», подавать их заново не нужно.`,
          )
        }
        lines.push('', 'Остальные музеи оставлены в списке — исправьте и отправьте заново.')
        resultMsg.value = lines.join('\n')

        // В списке оставляем неотправленное — БЕЗ кода группы в примечании,
        // код подставится заново при повторной отправке (он тот же).
        const sentIndexes = okResults.map(r => r.index)
        entries.value = all.filter((_, idx) => !sentIndexes.includes(idx))
        resetDraft()
      }
      showResult.value = true
    }

    return {
      todayStr,
      getFormatedLocale,
      draftQuote,
      entriesTotal,
      formatMoney,
      serviceTitle,
      country,
      isChinaGroup,
      isRussiaGroup,
      countrySearch,
      guideName,
      guidePhone,
      groupRefNumber,
      entries,
      draft,
      editingIdx,
      sameAsPrevious,
      draftForm,
      draftFormValid,
      datepickerKey,
      museumSearch,
      submitting,
      showResult,
      resultOk,
      resultMsg,
      selectedMuseumObject,
      guideFieldText,
      autoGuidesHint,
      filteredMuseumsList,
      museumObjectsList,
      scheduleList,
      filteredCountries,
      personsCount,
      rgAvailable,
      agAvailable,
      serviceLabel,
      serviceHint,
      rgThreshold,
      rgMandatory,
      isDraftValid,
      canSubmit,
      submitCount,
      onMuseumChange,
      onObjectChange,
      onDateChange,
      onClosedDayToggle,
      onSameAsPrevious,
      onServiceToggle,
      saveDraft,
      clearDraft,
      cancelEdit,
      editEntry,
      removeEntry,
      saveAttempted,
      missingFields,
      isDraftEmpty,
      submitAll,
      onResultClose,
      validators: { integerPositiveValidator },
      icons: { mdiClose, mdiHelpCircleOutline, mdiPencil },
    }
  },
}
</script>
